import clsx from "clsx";

import { sendTelemetryEvent } from "@/lib/telemetry";

import { defaultAttributes } from "../Component";
import type { IComponentAjax, IComponentAjaxState } from "../ComponentAjax";
import { ComponentAjax } from "../ComponentAjax";
import ImageLoader from "../ImageLoader";
import type { TelemetryProps } from "../Modules/Helpers";
import { redirectTo, requestTrack } from "../Modules/Helpers";
import type { IMainLoadingModule } from "../Modules/MainLoading";
import { showMainLoading } from "../Modules/MainLoading";
import { type ButtonAppearance } from "./Appearance";

import * as css from "./Button.module.css";

export interface IButton extends IComponentAjax<boolean>, IMainLoadingModule, TelemetryProps {
    name?: string;
    navigateUrl?: string;
    onClick?: (event: React.MouseEvent<HTMLElement>) => boolean | void;
    text?: string;
    value?: string;
    disabled?: boolean;
    appearance?: ButtonAppearance;
    isSubmit?: boolean;
    title?: string;
    target?: "_self" | "_blank";
    svgIcon?: string;
    onSuccess?: () => void;
    dataBrandId?: string;
    dataTestid?: string;
}

export class Button extends ComponentAjax<boolean, IButton, IComponentAjaxState> {
    public static defaultProps: IButton = {
        ...ComponentAjax.defaultProps,
        disabled: false,
        appearance: "magenta",
        isSubmit: false,
        hideMainLoading: false,
        loading: { isModal: true },
    };

    protected isPureComponent = true;

    constructor(props: IButton) {
        super(props);
        this.state = {
            isLoading: false,
        };
    }

    public render(): JSX.Element {
        const renderButton = () => {
            if (this.props.isSubmit === true) {
                const buttonProps: React.ButtonHTMLAttributes<HTMLButtonElement> = {
                    id: this.props.clientId ? `${this.props.clientId}-button` : undefined,
                    type: "submit",
                    name: this.props.name,
                    value: this.props.value,
                    disabled: this.props.disabled,
                    className: css.button,
                    onClick: this.onClick,
                    title: this.props.title,
                    formTarget: this.props.target,
                };
                if (this.props.svgIcon) {
                    return (
                        <button {...buttonProps} data-testid={this.props.dataTestid}>
                            <div className={css.buttonWithIcon}>
                                <div className={css.roundIcon}>
                                    <ImageLoader src={this.props.svgIcon} />
                                </div>
                                <div className={css.roundText}>{this.props.text}</div>
                            </div>
                        </button>
                    );
                }

                return (
                    <button {...buttonProps} data-testid={this.props.dataTestid}>
                        {this.props.text}
                    </button>
                );
            }

            const inputProps: React.DetailedHTMLProps<
                React.InputHTMLAttributes<HTMLInputElement>,
                HTMLInputElement
            > = {
                id: this.props.clientId ? `${this.props.clientId}-button` : undefined,
                type: "button",
                name: this.props.name,
                value: this.props.text,
                disabled: this.props.disabled,
                className: css.button,
                onClick: this.onClick,
                title: this.props.title,
                formTarget: this.props.target,
            };

            return (
                <input
                    {...inputProps}
                    data-brand-id={this.props.dataBrandId}
                    data-testid={this.props.dataTestid}
                />
            );
        };

        return (
            <div
                {...defaultAttributes(
                    this.props,
                    clsx(
                        css.buttonComponent,
                        css[this.props.appearance || "magenta"],
                        this.props.disabled ? css.disabled : undefined,
                    ),
                )}
            >
                {renderButton()}
                {this.renderLoading()}
                {this.props.ajaxAction ? super.renderAjaxErrorContainer() : null}
            </div>
        );
    }

    protected ajaxSuccess() {
        if (this.props.onSuccess) {
            this.props.onSuccess();
        }

        this.setState({ isLoading: false });
    }

    protected ajaxFail() {
        this.setState({ isLoading: false });
        return true;
    }

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        if (this.props.telemetryCustom) {
            sendTelemetryEvent(this.props.telemetryCustom);
        }

        requestTrack(this.props.trackingUrl);

        event.stopPropagation();

        if (this.props.onClick && this.props.onClick(event) === false) {
            return false;
        }

        if (this.props.isSubmit) {
            showMainLoading(this.props, event);
            return true;
        }

        if (this.props.ajaxAction) {
            this.setState({ isLoading: true });
            this.ajaxRequest();
        }

        if (this.props.navigateUrl && event.isDefaultPrevented() === false) {
            const newWindow = this.props.target === "_blank";

            if (newWindow !== true) {
                showMainLoading(this.props, event);
            }

            redirectTo(this.props.navigateUrl, this.props, newWindow);
        }

        return true;
    };
}
